/**
 * Dynamic dates for the fictional product mockups.
 * Anchor: October 14, 2026. Once that date passes, the deadline moves forward 90 days at a time
 * (→ Jan 12, 2027 → Apr 12, 2027 …), so the illustrative UI always shows the next upcoming deadline.
 * Tax year, case ID, invoice and settlement dates all derive from it.
 */
export interface DemoData {
  readonly year: number;
  readonly caseId: string;
  readonly invoice: string;
  readonly deadlineLong: string;
  readonly deadlineShort: string;
  readonly daysLeft: number;
  readonly offer: string;
  readonly offerLong: string;
  readonly counter: string;
  readonly counterLong: string;
}

const DAY_MS = 864e5;
const DEADLINE_STEP_DAYS = 90;
const OFFER_DAYS_BEFORE_DEADLINE = 26;
const COUNTER_DAYS_BEFORE_DEADLINE = 23;

const SHORT: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' };
const LONG: Intl.DateTimeFormatOptions = { month: 'long', day: 'numeric', year: 'numeric' };

export function createDemoData(now: Date = new Date()): DemoData {
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  let deadline = new Date(2026, 9, 14);
  while (deadline < today) {
    deadline = new Date(deadline.getFullYear(), deadline.getMonth(), deadline.getDate() + DEADLINE_STEP_DAYS);
  }
  const year = deadline.getFullYear();
  const format = (date: Date, options: Intl.DateTimeFormatOptions): string => date.toLocaleDateString('en-US', options);
  const daysBefore = (n: number): Date => new Date(deadline.getTime() - n * DAY_MS);
  return {
    year,
    caseId: `RT-${String(year).slice(-2)}01847`,
    invoice: `INV-${year}-0419`,
    deadlineLong: format(deadline, LONG),
    deadlineShort: format(deadline, SHORT),
    daysLeft: Math.max(0, Math.round((deadline.getTime() - today.getTime()) / DAY_MS)),
    offer: format(daysBefore(OFFER_DAYS_BEFORE_DEADLINE), SHORT),
    offerLong: format(daysBefore(OFFER_DAYS_BEFORE_DEADLINE), LONG),
    counter: format(daysBefore(COUNTER_DAYS_BEFORE_DEADLINE), SHORT),
    counterLong: format(daysBefore(COUNTER_DAYS_BEFORE_DEADLINE), { month: 'long', day: 'numeric' }),
  };
}

const dueLabel = (daysLeft: number): string =>
  daysLeft === 0 ? 'due today' : daysLeft === 1 ? 'in 1 day' : `in ${daysLeft} days`;

/** Replaces `{{CASE}}`, `{{DL_S}}`, … placeholders in rendered page HTML. Longer tokens are replaced first. */
export function fillDemoData(html: string, demo: DemoData = DEMO): string {
  return html
    .replace(/\{\{CASE\}\}/g, demo.caseId)
    .replace(/\{\{INV\}\}/g, demo.invoice)
    .replace(/\{\{DL_L\}\}/g, demo.deadlineLong)
    .replace(/\{\{DL_S\}\}/g, demo.deadlineShort)
    .replace(/\{\{OFFER_L\}\}/g, demo.offerLong)
    .replace(/\{\{OFFER\}\}/g, demo.offer)
    .replace(/\{\{COUNTER_L\}\}/g, demo.counterLong)
    .replace(/\{\{COUNTER\}\}/g, demo.counter)
    .replace(/\{\{DUE\}\}/g, dueLabel(demo.daysLeft))
    .replace(/\{\{Y\}\}/g, String(demo.year));
}

export const DEMO: DemoData = createDemoData();
